import React, { useState } from 'react';
import { Box, Chip, TextField, InputAdornment, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
interface User {
  id: number;
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  branchCode: string;
  settlementAccount?: string;
  designation?: string;
  agentCode: string;
  currency?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}
interface UsersTableProps {
  users: User[];
  onEditUser: (user: User) => void;
  onViewAuditLog: (user: User) => void;
  onStatusChange?: (user: User, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (user: User) => void;
  onSyncFromBank?: (user: User) => void;
  onViewResetHistory?: (user: User) => void;
  onResendEmail?: (user: User) => void;
}
interface Record {
  id: number;
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  branchCode: string;
  settlementAccount?: string;
  designation?: string;
  agentCode: string;
  currency?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}
interface RecordsTableProps {
  records: Record[];
  onEditRecord: (record: Record) => void;
  onViewAuditLog: (record: Record) => void;
  onStatusChange?: (record: Record, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (record: Record) => void;
  onSyncFromBank?: (record: Record) => void;
  onViewResetHistory?: (record: Record) => void;
  onResendEmail?: (record: Record) => void;
}
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
const AGENT_CODES = ['Ecobank Ghana', 'Ecobank Nigeria', 'Ecobank Senegal', 'Ecobank Uganda', 'Ecobank Kenya', "Ecobank Côte d'Ivoire", 'Ecobank Togo'];
const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onEditUser,
  onViewAuditLog,
  onStatusChange,
  onResetPassword,
  onSyncFromBank,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  // Define columns
  const columns: Column<User>[] = [{
    id: 'fullName',
    label: 'Full Name',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.fullName} onChange={e => setFilters({
      ...filters,
      fullName: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1490">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1491" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1489" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.username} onChange={e => setFilters({
      ...filters,
      username: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1493">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1494" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1492" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.email} onChange={e => setFilters({
      ...filters,
      email: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1496">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1497" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1495" />
  }, {
    id: 'mobile',
    label: 'Mobile',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.mobile} onChange={e => setFilters({
      ...filters,
      mobile: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1499">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1500" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1498" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || 'N/A'} size="small" sx={{
      backgroundColor: row.branchCode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(148, 163, 184, 0.1)',
      color: row.branchCode ? '#06B6D4' : '#94A3B8',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1501" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1503">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1504" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1502" />
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1505">
          {row.settlementAccount || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.settlementAccount} onChange={e => setFilters({
      ...filters,
      settlementAccount: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1507">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1508" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1506" />
  }, {
    id: 'designation',
    label: 'Designation',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1509">
          {row.designation || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.designation} onChange={e => setFilters({
      ...filters,
      designation: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1511">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1512" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1510" />
  }, {
    id: 'currency',
    label: 'Currency',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1513">
          {row.currency || 'USD'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.currency || ''} onChange={e => setFilters({
      ...filters,
      currency: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1515">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1516" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1514" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1517" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1518">
          <Select value={filters.status || 'all'} onChange={e => setFilters({
        ...filters,
        status: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1519">
            <MenuItem value="all" data-id="element-1520">All</MenuItem>
            <MenuItem value="active" data-id="element-1521">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1522">Inactive</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'createdAt',
    label: 'Created At',
    renderCell: row => new Date(row.createdAt).toLocaleDateString(),
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-1523">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-1524">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1525" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1526" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-1527">
              {dateRangeError}
            </Typography>}
        </Box>
  }];
  // Apply filters
  const filteredUsers = users.filter(user => {
    if (filters.fullName && !user.fullName.toLowerCase().includes(filters.fullName.toLowerCase())) return false;
    if (filters.username && !user.username.toLowerCase().includes(filters.username.toLowerCase())) return false;
    if (filters.email && !user.email.toLowerCase().includes(filters.email.toLowerCase())) return false;
    if (filters.mobile && !user.mobile.includes(filters.mobile)) return false;
    if (filters.branchCode && !user.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase())) return false;
    if (filters.settlementAccount && !user.settlementAccount?.toLowerCase().includes(filters.settlementAccount.toLowerCase())) return false;
    if (filters.designation && !user.designation?.toLowerCase().includes(filters.designation.toLowerCase())) return false;
    if (filters.status && user.status !== filters.status) return false;
    if (filters.dateFrom) {
      const userDate = new Date(user.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (userDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const userDate = new Date(user.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (userDate > toDate) return false;
    }
    return true;
  });
  const filterChips: FilterChip[] = [...(filters.fullName ? [{
    id: 'fullName',
    label: 'Name',
    value: filters.fullName,
    onDelete: () => setFilters({
      ...filters,
      fullName: ''
    })
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => setFilters({
      ...filters,
      username: ''
    })
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => setFilters({
      ...filters,
      email: ''
    })
  }] : []), ...(filters.branchCode ? [{
    id: 'branchCode',
    label: 'Branch Code',
    value: filters.branchCode,
    onDelete: () => setFilters({
      ...filters,
      branchCode: ''
    })
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => setFilters({
      ...filters,
      settlementAccount: ''
    })
  }] : []), ...(filters.designation ? [{
    id: 'designation',
    label: 'Designation',
    value: filters.designation,
    onDelete: () => setFilters({
      ...filters,
      designation: ''
    })
  }] : []), ...(filters.status ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: ''
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : [])];
  // Row actions
  const rowActions: RowAction<User>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: user => console.log('View user', user)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditUser
  }, {
    icon: 'lucide:refresh-cw',
    label: 'Sync from Bank',
    onClick: user => onSyncFromBank?.(user)
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: user => onResendEmail?.(user),
    condition: user => shouldShowResendEmail(user)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: user => onStatusChange?.(user, 'inactive'),
    condition: user => user.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: user => onStatusChange?.(user, 'active'),
    condition: user => user.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: user => onResetPassword?.(user)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: user => onViewResetHistory?.(user)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: onViewAuditLog
  }];
  return <DataTable title="Users" data={filteredUsers} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={() => setFilters({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })} initialVisibleColumns={['fullName', 'username', 'email', 'mobile', 'branchCode', 'settlementAccount', 'designation', 'currency', 'status', 'createdAt']} onExport={() => console.log('Export CSV')} emptyStateMessage="No users found" data-id="element-1528" />;
};
const RecordsTable: React.FC<RecordsTableProps> = ({
  records,
  onEditRecord,
  onViewAuditLog,
  onStatusChange,
  onResetPassword,
  onSyncFromBank,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  // Define columns
  const columns: Column<Record>[] = [{
    id: 'fullName',
    label: 'Full Name',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.fullName} onChange={e => setFilters({
      ...filters,
      fullName: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1530">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1531" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1529" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.username} onChange={e => setFilters({
      ...filters,
      username: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1533">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1534" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1532" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.email} onChange={e => setFilters({
      ...filters,
      email: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1536">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1537" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1535" />
  }, {
    id: 'mobile',
    label: 'Mobile',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.mobile} onChange={e => setFilters({
      ...filters,
      mobile: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1539">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1540" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1538" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || 'N/A'} size="small" sx={{
      backgroundColor: row.branchCode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(148, 163, 184, 0.1)',
      color: row.branchCode ? '#06B6D4' : '#94A3B8',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1541" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1543">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1544" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1542" />
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1545">
          {row.settlementAccount || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.settlementAccount} onChange={e => setFilters({
      ...filters,
      settlementAccount: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1547">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1548" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1546" />
  }, {
    id: 'designation',
    label: 'Designation',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1549">
          {row.designation || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.designation} onChange={e => setFilters({
      ...filters,
      designation: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1551">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1552" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1550" />
  }, {
    id: 'currency',
    label: 'Currency',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1553">
          {row.currency || 'USD'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.currency || ''} onChange={e => setFilters({
      ...filters,
      currency: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1555">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1556" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1554" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1557" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1558">
          <Select value={filters.status || 'all'} onChange={e => setFilters({
        ...filters,
        status: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1559">
            <MenuItem value="all" data-id="element-1560">All</MenuItem>
            <MenuItem value="active" data-id="element-1561">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1562">Inactive</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'createdAt',
    label: 'Created At',
    renderCell: row => new Date(row.createdAt).toLocaleDateString(),
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-1563">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-1564">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1565" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1566" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-1567">
              {dateRangeError}
            </Typography>}
        </Box>
  }];
  // Apply filters
  const filteredRecords = records.filter(record => {
    if (filters.fullName && !record.fullName.toLowerCase().includes(filters.fullName.toLowerCase())) return false;
    if (filters.username && !record.username.toLowerCase().includes(filters.username.toLowerCase())) return false;
    if (filters.email && !record.email.toLowerCase().includes(filters.email.toLowerCase())) return false;
    if (filters.mobile && !record.mobile.includes(filters.mobile)) return false;
    if (filters.branchCode && !record.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase())) return false;
    if (filters.settlementAccount && !record.settlementAccount?.toLowerCase().includes(filters.settlementAccount.toLowerCase())) return false;
    if (filters.designation && !record.designation?.toLowerCase().includes(filters.designation.toLowerCase())) return false;
    if (filters.status && record.status !== filters.status) return false;
    if (filters.dateFrom) {
      const recordDate = new Date(record.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (recordDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const recordDate = new Date(record.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (recordDate > toDate) return false;
    }
    return true;
  });
  const filterChips: FilterChip[] = [...(filters.fullName ? [{
    id: 'fullName',
    label: 'Name',
    value: filters.fullName,
    onDelete: () => setFilters({
      ...filters,
      fullName: ''
    })
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => setFilters({
      ...filters,
      username: ''
    })
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => setFilters({
      ...filters,
      email: ''
    })
  }] : []), ...(filters.branchCode ? [{
    id: 'branchCode',
    label: 'Branch Code',
    value: filters.branchCode,
    onDelete: () => setFilters({
      ...filters,
      branchCode: ''
    })
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => setFilters({
      ...filters,
      settlementAccount: ''
    })
  }] : []), ...(filters.designation ? [{
    id: 'designation',
    label: 'Designation',
    value: filters.designation,
    onDelete: () => setFilters({
      ...filters,
      designation: ''
    })
  }] : []), ...(filters.status ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: ''
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : [])];
  // Row actions
  const rowActions: RowAction<Record>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: record => console.log('View record', record)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditRecord
  }, {
    icon: 'lucide:refresh-cw',
    label: 'Sync from Bank',
    onClick: record => onSyncFromBank?.(record)
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: record => onResendEmail?.(record),
    condition: record => shouldShowResendEmail(record)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: record => onStatusChange?.(record, 'inactive'),
    condition: record => record.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: record => onStatusChange?.(record, 'active'),
    condition: record => record.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: record => onResetPassword?.(record)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: record => onViewResetHistory?.(record)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: onViewAuditLog
  }];
  return <DataTable title="Records" data={filteredRecords} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={() => setFilters({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })} initialVisibleColumns={['fullName', 'username', 'email', 'mobile', 'branchCode', 'settlementAccount', 'designation', 'currency', 'status', 'createdAt']} onExport={() => console.log('Export CSV')} emptyStateMessage="No records found" data-id="element-1568" />;
};
export default RecordsTable;